import { SWRConfiguration } from 'swr';
import { capturePromiseException } from '~/lib/metrics';

/** How often the notification counts refresh once a fetch has succeeded. */
export const BACKGROUND_TASK_REFRESH_MS = 15 * 60000;

/**
 * Reports the error, then re-throws so SWR treats the fetch as failed.
 *
 * Swallowing it (the previous `.catch(capturePromiseException)`) left the response `undefined`,
 * so the `res.success` check immediately after threw a confusing TypeError instead of the real
 * error - and, worse, left `data` undefined, which is what triggered the 100ms retry loop.
 *
 * Returning `never` also narrows the awaited value back to a plain `ApiResponse<T>`, so callers
 * do not need to guard a value the type already claims is always present.
 */
export function reportAndRethrow(e: unknown): never {
  capturePromiseException(e);
  throw e;
}

/**
 * Shared SWR policy for the notification background tasks (surcharges, refunds, claims,
 * support). These poll low-priority badge counts, so a failure should never cost more than the
 * data is worth.
 *
 * It previously used `refreshInterval: (d) => (d === undefined ? 100 : 15 * 60000)`. Because a
 * failed fetch leaves `data` undefined, any error pinned the hook at ten requests a second for
 * as long as the tab stayed focused. That is self-sustaining: the retries add load, the load
 * causes more failures, and the failures keep `data` undefined. Four such tasks run at once.
 *
 * The 100ms tick was also doing double duty as the initial load, since `revalidateOnMount` was
 * false. That job now sits where it belongs, and failures fall to SWR's randomised exponential
 * backoff instead.
 */
export const backgroundTaskSwrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  // Performs the initial load that the 100ms refreshInterval used to stand in for.
  revalidateOnMount: true,
  refreshWhenHidden: false,
  refreshInterval: BACKGROUND_TASK_REFRESH_MS,
  // SWR scales this by 2^attempt with jitter, giving roughly 5s, 10s, 20s, 40s, 80s before it
  // stops and waits for the next scheduled refresh.
  errorRetryInterval: 5000,
  errorRetryCount: 5
};
